import PropTypes from "prop-types";
import React, { useEffect, useRef, useCallback } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import SimpleBar from "simplebar-react";
import MetisMenu from "metismenujs";
import withRouter from "components/Common/withRouter";
import { withTranslation } from "react-i18next";
import { getRole3SidebarItems, getSidebarItemIconClass } from "../../helpers/companyPortalConfig";
import { useMustRead } from "components/MustRead/MustReadProvider";
import "components/MustRead/mustRead.css";

const SidebarContent = (props) => {
  const ref = useRef();
  const location = useLocation();
  const navigate = useNavigate();
  const pendingScrollTargetRef = useRef(null);

  const { user } = useSelector(state => state.Login);
  const role = Number(user?.role ?? 3);
  const canSee = (allowed) => allowed.includes(role);
  const role3SidebarItems = getRole3SidebarItems(user?.company?.sidebarConfig);
  const { data: mustRead } = useMustRead();
  const mustReadOutstanding = Math.max(0, (mustRead?.total || 0) - (mustRead?.reviewed || 0));

  const scrollElement = (item) => {
    if (item && ref.current) {
      const currentPosition = item.offsetTop;
      const sb = ref.current.getScrollElement?.() || ref.current.el; // simplebar instance
      if (sb && currentPosition > window.innerHeight) {
        sb.scrollTop = currentPosition - 300;
      }
    }
  };

  const activateParentDropdown = useCallback((item) => {
    if (!item) return;
    item.classList.add("active");
    const parent = item.parentElement;
    const parent2El = parent?.childNodes?.[1];

    if (parent2El && parent2El.id !== "side-menu") {
      parent2El.classList.add("mm-show");
    }

    if (parent) {
      parent.classList.add("mm-active");
      const parent2 = parent.parentElement;

      if (parent2) {
        parent2.classList.add("mm-show"); // ul
        const parent3 = parent2.parentElement; // li

        if (parent3) {
          parent3.classList.add("mm-active");
          parent3.childNodes[0]?.classList.add("mm-active"); // a
          const parent4 = parent3.parentElement; // ul
          if (parent4) {
            parent4.classList.add("mm-show");
            const parent5 = parent4.parentElement; // li
            if (parent5) {
              parent5.classList.add("mm-show");
              parent5.childNodes[0]?.classList.add("mm-active"); // a
            }
          }
        }
      }
    }
    scrollElement(item);
  }, []);

  const removeActivation = (items) => {
    for (let i = 0; i < items.length; ++i) {
      const item = items[i];
      const parent = item.parentElement;

      if (item?.classList.contains("active")) item.classList.remove("active");

      if (parent) {
        const parent2El =
          parent.childNodes && parent.childNodes.length > 1 ? parent.childNodes[1] : null; // <- fixed length
        if (parent2El && parent2El.id !== "side-menu") {
          parent2El.classList.remove("mm-show");
        }

        parent.classList.remove("mm-active");
        const parent2 = parent.parentElement;

        if (parent2) {
          parent2.classList.remove("mm-show");

          const parent3 = parent2.parentElement;
          if (parent3) {
            parent3.classList.remove("mm-active");
            parent3.childNodes[0]?.classList.remove("mm-active");

            const parent4 = parent3.parentElement;
            if (parent4) {
              parent4.classList.remove("mm-show");
              const parent5 = parent4.parentElement;
              if (parent5) {
                parent5.classList.remove("mm-show");
                parent5.childNodes[0]?.classList.remove("mm-active");
              }
            }
          }
        }
      }
    }
  };

  const activeMenu = useCallback(() => {
    const pathName = location.pathname;
    const ul = document.getElementById("side-menu");
    if (!ul) return;

    const items = ul.getElementsByTagName("a");
    removeActivation(items);

    let matchingMenuItem = null;
    for (let i = 0; i < items.length; ++i) {
      // match by pathname only
      if (items[i].pathname === window.location.origin + pathName) {
        matchingMenuItem = items[i];
        break;
      }
      // fallback: relative compare
      if (items[i].getAttribute("href") === pathName) {
        matchingMenuItem = items[i];
        break;
      }
    }
    if (matchingMenuItem) activateParentDropdown(matchingMenuItem);
  }, [location.pathname, activateParentDropdown]);

  const scrollToContactInfoSection = () => {
    const contactInfoSection = document.getElementById("contact-info");
    if (!contactInfoSection) return;
    contactInfoSection.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "start",
    });
  };

  const scrollToSupportInfoSection = () => {
    const supportInfoSection = document.getElementById("support-info");
    if (!supportInfoSection) return;
    supportInfoSection.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "start",
    });
  };

  const scrollToTargetSection = (targetId) => {
    if (!targetId) return;
    if (targetId === "contact-info") {
      scrollToContactInfoSection();
      return;
    }
    if (targetId === "support-info") {
      scrollToSupportInfoSection();
      return;
    }
    const target = document.getElementById(targetId);
    if (!target) return;
    target.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "start",
    });
  };

  const scrollToSidebarTarget = (targetId) => (event) => {
    event?.preventDefault();

    if (location.pathname === "/home") {
      scrollToTargetSection(targetId);
      return;
    }

    pendingScrollTargetRef.current = targetId;
    navigate("/home");
  };

  useEffect(() => {
    if (location.pathname !== "/home") return;

    if (pendingScrollTargetRef.current) {
      const targetId = pendingScrollTargetRef.current;
      pendingScrollTargetRef.current = null;
      requestAnimationFrame(() => {
        scrollToTargetSection(targetId);
      });
      return;
    }

    const hashId = location.hash?.replace(/^#/, "");
    if (hashId === "support-info" || hashId === "contact-info") {
      requestAnimationFrame(() => {
        scrollToTargetSection(hashId);
      });
    }
  }, [location.pathname, location.hash]);

  const closeSidebar = () => {
    const body = document.body;
    body.classList.remove("sidebar-enable");
    body.classList.remove("vertical-collpsed");
  };

  useEffect(() => {
    const handler = (e) => {
      const body = document.body;
      if (!body.classList.contains("sidebar-enable")) return; // menu closed, ignore

      // Ignore clicks on the sidebar toggle buttons to avoid double-toggling
      if (e.target.closest("#vertical-menu-btn") || e.target.closest("#vertical-menu-btn-mobile")) {
        return;
      }

      // Sidebar container (outside SidebarContent bhi chalega)
      const sidebarEl = document.querySelector(".vertical-menu");
      if (sidebarEl && !sidebarEl.contains(e.target)) {
        closeSidebar();
      }
    };

    // capture phase: zyada reliable (MetisMenu / React handlers se pehle)
    document.addEventListener("click", handler, { capture: true });
    document.addEventListener("touchstart", handler, { capture: true, passive: true });
    return () => {
      document.removeEventListener("click", handler, { capture: true });
      document.removeEventListener("touchstart", handler, { capture: true });
    };
  }, []);


  useEffect(() => {
    ref.current?.recalculate?.();
  }, []);

  useEffect(() => {
    new MetisMenu("#side-menu");
    activeMenu();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    activeMenu();
  }, [activeMenu]);

  const { t } = props;

  const onboardingLink = canSee([3, 4]) ? (
    <li key="onboarding">
      <Link to="/must-read">
        <i className="bx bx-list-check" />
        <span style={{ fontWeight: mustReadOutstanding > 0 ? 700 : 400 }}>{t("Onboarding")}</span>
        {mustReadOutstanding > 0 && (
          <span className="badge rounded-pill float-end mr-sidebar-count" aria-label={`${mustReadOutstanding} to review`}>
            {mustReadOutstanding}
          </span>
        )}
      </Link>
    </li>
  ) : null;

  return (
    <React.Fragment>
      <SimpleBar className="h-100" ref={ref}>
        <div id="sidebar-menu">
          <ul
            className="metismenu list-unstyled"
            id="side-menu"
            onClick={(e) => {
              const a = e.target.closest("a");
              if (!a) return;
              if (window.innerWidth <= 998) {
                closeSidebar();
              }
            }}
          >
            <li className="menu-title">{t("Menu")}</li>

            {/* Dashboard: roles 1,2 */}
            {canSee([1, 2]) && (
              <li>
                <Link to="/dashboard">
                  <i className="bx bx-home" />
                  <span>Dashboard</span>
                </Link>
              </li>
            )}

            {/* Companies: role 1 only */}
            {canSee([1]) && (
              <li>
                <Link to="/companies">
                  <i className="bx bx-book-content" />
                  <span>{t("Companies")}</span>
                </Link>
              </li>
            )}
            {canSee([1, 2]) && (
              <li>
                <Link to="/blacklist">
                  <i className="bx bx-block" />
                  <span>{t("Blacklist")}</span>
                </Link>
              </li>
            )}
            {canSee([1, 2]) && (
              <li>
                <Link to="/distributor-whitelist">
                  <i className="bx bx-check-shield" />
                  <span>{t("Distributor Whitelist")}</span>
                </Link>
              </li>
            )}

            {/* Documents: roles 1,2 */}
            {canSee([1, 2]) && (
              <li>
                <Link to="/resources">
                  <i className="bx bx-aperture" />
                  <span>{t("Resources")}</span>
                </Link>
              </li>
            )} 
            {canSee([3, 4]) &&
              role3SidebarItems.flatMap((item) => {
                let node = null;
                if (item.type === "route") {
                  node = (
                    <li key={item.id}>
                      <Link to={item.path}>
                        <i className={getSidebarItemIconClass(item)} />
                        <span>{t(item.label)}</span>
                      </Link>
                    </li>
                  );
                } else if (item.type === "scroll") {
                  node = (
                    <li key={item.id}>
                      <a href={`#${item.scrollTarget}`} onClick={scrollToSidebarTarget(item.scrollTarget)}>
                        <i className={getSidebarItemIconClass(item)} />
                        <span>{t(item.label)}</span>
                      </a>
                    </li>
                  );
                } else if (item.type === "external") {
                  node = (
                    <li key={item.id}>
                      <a
                        href={item.href}
                        target={item.openInNewTab === false ? undefined : "_blank"}
                        rel={item.openInNewTab === false ? undefined : "noopener noreferrer"}
                      >
                        <i className={getSidebarItemIconClass(item)} />
                        <span>{t(item.label)}</span>
                      </a>
                    </li>
                  );
                }

                if (!node) return [];
                if (item.id === "home" || item.path === "/home") return [node, onboardingLink];
                return [node];
              })}
            {canSee([3, 4]) &&
              !role3SidebarItems.some((item) => item.id === "home" || item.path === "/home") &&
              onboardingLink}

            {/* Users: roles 1,2 */}
            {canSee([1, 2]) && (
              <li>
                <Link to="/users">
                  <i className="bx bx-user-plus" />
                  <span>{t("Users")}</span>
                </Link>
              </li>
            )}
            {canSee([1, 2]) && (
              <li>
                <Link to="/must-read-docs">
                  <i className="bx bx-book-open" />
                  <span>{t("Onboarding")}</span>
                </Link>
              </li>
            )}

            {/* Profile: roles 1,2 */}
            {canSee([1, 2]) && (
              <li>
                <Link to="/profile">
                  <i className="bx bx-user" />
                  <span>{t("Profile")}</span>
                </Link>
              </li>
            )}
          </ul>
        </div>
      </SimpleBar>
    </React.Fragment>
  );
};

SidebarContent.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};

export default withRouter(withTranslation()(SidebarContent));
